import { Info, WarningOctagon } from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import Button from '../../atoms/Button';
import { BannerProps } from './Banner.types';

export const Banner = ({
  type = 'error',
  title,
  description,
  additionalInfo,
  dismiss,
  cta,
  style = 'fill',
  onDismiss,
  onCta,
  className,
}: BannerProps) => {
  const bgColor = {
    error: 'bg-danger-surface',
    info: 'bg-blue-200',
    success: 'bg-green-300 dark:bg-positive-surface',
    warning: 'bg-warning-surface',
    errorBlackBg: 'bg-[#2D0F0F]/50',
    infoBlackBg: 'bg-[#172554]',
  };

  const borderColor = {
    error: 'border-red-700 dark:border-danger',
    info: 'border-blue-1000 dark:border-link',
    success: 'border-green-600 dark:border-positive',
    warning: 'border-warning',
    errorBlackBg: 'border-orange-900',
    infoBlackBg: 'border-[#2563EB]',
  };

  const iconConfig = {
    error: <WarningOctagon size={20} weight="fill" color="#D3171E" />,
    info: (
      <Info size={20} weight="fill" className="text-blue-1000 dark:text-link" />
    ),
    success: <Info size={20} weight="fill" className="text-positive" />,
    warning: <Info size={20} weight="fill" color="#D67200" />,
    errorBlackBg: <WarningOctagon size={20} weight="fill" color="#D3171E" />,
    infoBlackBg: <Info size={20} weight="fill" color="#2563EB" />,
  };

  const buttonTextColor = {
    error: 'text-red-700 dark:text-danger dark:active:text-content-muted',
    info: 'text-blue-1000 dark:text-link dark:active:text-content-muted',
    success: 'text-positive',
    warning: 'text-yellow-700 dark:text-warning dark:active:text-content-muted',
    errorBlackBg: 'text-white',
    infoBlackBg: 'text-white',
  };

  const isDarkBg = type === 'errorBlackBg' || type === 'infoBlackBg';
  const isErrorState = type === 'error' || type === 'errorBlackBg';
  const hasLightOnlyFill = style === 'fill' && type === 'info';
  const textColor = isDarkBg ? 'text-white' : 'text-content';
  const additionalInfoColor = isDarkBg ? 'text-white' : 'text-content-subtle';

  const renderBannerActions = () => {
    return (
      <>
        {cta && (
          <Button.v2
            label={cta}
            size="xsmall"
            shape="square"
            variant="tertiary-grey-monochrome"
            onClick={onCta}
            aria-describedby={type}
            className={buttonTextColor[type]}
          />
        )}

        <Button.v2
          onClick={onDismiss}
          variant="tertiary-grey-monochrome"
          size="xsmall"
          shape="square"
          label={dismiss}
          className="ml-2 w-auto"
          aria-describedby={type}
        />
      </>
    );
  };

  return (
    <div
      className={c(
        `${style === 'outline' ? `border ${borderColor[type]}` : ''} flex flex-wrap items-start rounded-lg md:flex-nowrap ${style === 'fill' ? bgColor[type] : 'bg-surface'} p-3 text-sm leading-5 tracking-[-0.028px]`,
        hasLightOnlyFill && 'light',
        className
      )}
      role={
        isErrorState ? 'alert' : type === 'infoBlackBg' ? 'status' : undefined
      }
      aria-live={
        isErrorState
          ? 'assertive'
          : type === 'infoBlackBg'
            ? 'polite'
            : undefined
      }
      tabIndex={isErrorState || type === 'infoBlackBg' ? -1 : undefined}
      data-test={
        isErrorState
          ? 'server-message-alert'
          : type === 'infoBlackBg'
            ? 'limit-message-alert'
            : undefined
      }
    >
      <div className="mr-3 shrink-0">{iconConfig[type]}</div>

      <div className="flex flex-1 flex-wrap items-start md:flex-nowrap md:items-center">
        <div className="flex flex-1 flex-col">
          <div>
            <h2
              className={`font-bold ${textColor}`}
              id={isErrorState ? 'server-message-title' : undefined}
            >
              {title}
            </h2>
            <p className={`pb-2 pt-2 font-normal ${textColor}`}>
              {description}
            </p>
            {additionalInfo && (
              <p className={`font-normal ${additionalInfoColor}`}>
                {additionalInfo}
              </p>
            )}
          </div>

          <div className="mt-3 md:hidden">{renderBannerActions()}</div>
        </div>

        <div className="hidden items-center md:flex">
          {renderBannerActions()}
        </div>
      </div>
    </div>
  );
};
